import { useTranslation } from 'react-i18next'

import {
  type UpdateMovePayload,
  useCurrentMove,
  useUpdateMoveAction,
} from '../../../modules/moves'
import { useNavigate } from '../../../modules/navigation'
import { useDateFormatter } from '../../../utils/date'
import { type EditableSectionProps } from '../../EditableSections/editableSections.type'
import {
  Label,
  Value,
  Values,
  Row,
  Cell,
} from '../../data'
import DateTimeForm from '../../forms/editmove/DateTimeForm'
import { sortByNumericValues } from '../../../utils/sort'
import Route from '../../../app/Route.enum'
import { useErrorFormatter } from '../../errors/useErrorFormatter'
import { errorNotification, successNotification } from '../../ToastNotifications'
import { getJobDate, getJobLabel } from '../../../modules/moves/core/resolved.utils'
import { mergeClassName } from '../../../utils/mergeClassName'
import { ArrivalWindow } from '../../../common/arrivalWindow'

const DateTimeSection: React.FC<EditableSectionProps> = ({
  isEditing,
  onClose,
}) => {
  const { t } = useTranslation()
  const move = useCurrentMove()
  const formatDate = useDateFormatter()
  const [updateMove] = useUpdateMoveAction()
  const { navigate } = useNavigate()
  const formatError = useErrorFormatter()

  const onSubmit = async (data: UpdateMovePayload) => {
    try {
      await updateMove(data)
      onClose()
      navigate(Route.Move)
      successNotification((
        <>
          { t('components.move.dateTime.success') }
          <div className="font-bold">
            { t('components.move.quoteRecalculated') }
          </div>
        </>
      ))
    } catch (error) {
      errorNotification(formatError(error))
    }
  }

  if (isEditing) {
    return (
      <DateTimeForm
        onSubmit={onSubmit}
        onCancel={onClose}
      />
    )
  }

  const multiDays = move.jobs.length > 1

  return move.jobs.map((job, index) => {
    const jobDate = getJobDate(move, job.id)
    const preferredTime = sortByNumericValues(job.dateTime.preferredTimeRanges ?? [])
    const jobLabel = getJobLabel(move, job.id, t)

    return (
      <div key={job.id}>
        { multiDays && (
          <div
            className={mergeClassName(
              'mb-2 font-body2 text-lg text-black sm:mb-0 py-2',
              index > 0 && 'border-t pt-6 mt-6',
            )}
          >
            { jobLabel }
          </div>
        ) }
        <Row>
          <Cell>
            <Label>
              { multiDays
                ? t('components.move.dateTime.jobDate')
                : t('components.move.dateTime.movingDate') }
            </Label>
            <Value>{ formatDate(jobDate) }</Value>
          </Cell>
          <Cell>
            <Label>
              { t('components.move.dateTime.preferredTime') }
            </Label>
            { (preferredTime.length === 0 || preferredTime.length === Object.values(ArrivalWindow).length) && (
              <Value>
                <div className="text-neutral-400">
                  { t('arrivalWindows.any') }
                </div>
              </Value>
            ) }
            { preferredTime.length > 0 && (
              <Values>
                { preferredTime?.map(preferredTime => t(`arrivalWindows.${String(preferredTime)}.title`)) }
              </Values>
            ) }
          </Cell>
        </Row>
      </div>
    )
  })
}

export default DateTimeSection
